import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { useAuth } from './context/AuthContext';
import Layout from './components/layout/Layout.jsx';
import DashboardLayout from './components/layout/DashboardLayout.jsx';
import ProtectedRoute from './components/common/ProtectedRoute.jsx';
import ErrorBoundary from './components/common/ErrorBoundary.jsx';
import Home from './pages/home/Home.jsx';
import About from './pages/about/About.jsx';
import Admission from './pages/admission/Admission.jsx';
import AdmissionForm from './pages/admission/AdmissionForm.jsx';
import ApplicationStatus from './pages/admission/ApplicationStatus.jsx';
import AdmissionSuccess from './pages/admission/AdmissionSuccess.jsx';
import Contact from './pages/contact/Contact.jsx';
import Login from './pages/auth/Login.jsx';
import ResetPassword from './pages/auth/ResetPassword.jsx';

// Dashboard Pages
import StudentDashboard from './pages/dashboard/StudentDashboard.jsx';
import TeacherDashboard from './pages/dashboard/TeacherDashboard.jsx';
import ParentDashboard from './pages/dashboard/ParentDashboard.jsx';
import StaffDashboard from './pages/dashboard/StaffDashboard.jsx';

// Role-specific Pages
import CoursesPage from './pages/student/CoursesPage.jsx';
import StudentsPage from './pages/teacher/StudentsPage.jsx';
import TeacherReportsPage from './pages/teacher/ReportsPage.jsx';
import ProgressPage from './pages/parent/ProgressPage.jsx';
import PaymentsPage from './pages/parent/PaymentsPage.jsx';
import UsersPage from './pages/staff/UsersPage.jsx';
import EventsPage from './pages/staff/EventsPage.jsx';
import StaffReportsPage from './pages/staff/ReportsPage.jsx';
import AssignedClassesPage from './pages/staff/AssignedClassesPage.jsx';
import AssignedSubjectsPage from './pages/staff/AssignedSubjectsPage.jsx';
import StudentResultsPage from './pages/staff/StudentResultsPage.jsx';

// 404 Page
import NotFound from './pages/NotFound.jsx';
import ExamsPage from './pages/exams/ExamsPage';
import TakeExam from './pages/exams/TakeExam';
import ExamResults from './pages/exams/ExamResults';

// Academic Pages
import ClassesPage from './pages/academic/ClassesPage.jsx';
import ClassDetailPage from './pages/academic/ClassDetailPage.jsx';
import SubjectsPage from './pages/academic/SubjectsPage.jsx';
import TimetablePage from './pages/academic/TimetablePage.jsx';

// Assignment Pages
import StudentAssignments from './pages/assignments/StudentAssignments.jsx';
import TeacherAssignments from './pages/assignments/TeacherAssignments.jsx';

// Exam Pages
import StudentExams from './pages/exams/StudentExams.jsx';
import TeacherExams from './pages/exams/TeacherExams.jsx';
import StaffExams from './pages/exams/StaffExams.jsx';

// Other Pages
import GradesPage from './pages/grades/GradesPage.jsx';
import AttendancePage from './pages/attendance/AttendancePage.jsx';
import MessagesPage from './pages/messages/MessagesPage.jsx';
import AnnouncementsPage from './pages/announcements/AnnouncementsPage.jsx';
import LibraryPage from './pages/library/LibraryPage.jsx';
import ProfilePage from './pages/profile/ProfilePage.jsx';
import SettingsPage from './pages/settings/SettingsPage.jsx';
import NotificationsPage from './pages/notifications/NotificationsPage.jsx';

// Parent-specific pages
import ManageChildren from './pages/parent/ManageChildren.jsx';

// CBT Pages
import CBTLogin from './pages/cbt/CBTLogin.jsx';
import CBTWaitingRoom from './pages/cbt/CBTWaitingRoom.jsx';
import CBTExam from './pages/cbt/CBTExam.jsx';
import CBTSubmissionSuccess from './pages/cbt/CBTSubmissionSuccess.jsx';

// Detail/CRUD pages
import AssignmentDetail from './pages/assignments/AssignmentDetail.jsx';
import AssignmentCreate from './pages/assignments/AssignmentCreate.jsx';
import AssignmentGrade from './pages/assignments/AssignmentGrade.jsx';
import ExamDetail from './pages/exams/ExamDetail.jsx';
import ExamCreate from './pages/exams/ExamCreate.jsx';
import ResourceDetail from './pages/library/ResourceDetail.jsx';
import ResourceUpload from './pages/library/ResourceUpload.jsx';
import ComposeMessage from './pages/messages/ComposeMessage.jsx';
import MessageThread from './pages/messages/MessageThread.jsx';
import AttendanceAnalytics from './pages/attendance/AttendanceAnalytics.jsx';
import MarkAttendance from './pages/attendance/MarkAttendance.jsx';

// Role-based redirect component
const RoleBasedRedirect = () => {
  const { user } = useAuth();
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  const role = user.role;
  
  switch (role) {
    case 'student':
      return <Navigate to="/dashboard/student" replace />;
    case 'parent':
      return <Navigate to="/dashboard/parent" replace />;
    case 'teacher':
    case 'staff':
    case 'admin':
      // All non-student, non-parent roles use staff dashboard
      return <Navigate to="/dashboard/staff" replace />;
    default:
      return <Navigate to="/dashboard/student" replace />;
  }
};

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <div className="bg-background text-foreground h-full">
          <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Layout />}>
            <Route index element={<Home />} />
            <Route path="about" element={<About />} />
            <Route path="admission" element={<Admission />} />
            <Route path="admission/form" element={<AdmissionForm />} />
            <Route path="admission/status" element={<ApplicationStatus />} />
            <Route path="admission/success" element={<AdmissionSuccess />} />
            <Route path="contact" element={<Contact />} />
            <Route path="login" element={<Login />} />
          </Route>

          {/* Auth Routes - Outside Layout */}
          <Route path="/reset-password/:token" element={<ResetPassword />} />

          {/* CBT Routes - Public access for exam taking */}
          <Route path="/cbt" element={<CBTLogin />} />
          <Route path="/cbt/waiting/:examId" element={<CBTWaitingRoom />} />
          <Route path="/cbt/exam/:examId" element={<CBTExam />} />
          <Route path="/cbt/success" element={<CBTSubmissionSuccess />} />
          
          {/* Protected Dashboard Routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }>
            {/* Dashboard Pages */}
            <Route index element={<RoleBasedRedirect />} />
            <Route path="student" element={
              <ProtectedRoute requiredRoles={['student']}>
                <StudentDashboard />
              </ProtectedRoute>
            } />
            <Route path="teacher" element={
              <ProtectedRoute requiredRoles={['teacher']}>
                <TeacherDashboard />
              </ProtectedRoute>
            } />
            <Route path="parent" element={
              <ProtectedRoute requiredRoles={['parent']}>
                <ParentDashboard />
              </ProtectedRoute>
            } />
            <Route path="staff" element={
              <ProtectedRoute requiredRoles={['staff', 'teacher', 'admin']}>
                <StaffDashboard />
              </ProtectedRoute>
            } />

            {/* Role-specific Routes */}
            {/* Student Routes */}
            <Route path="classes" element={
              <ProtectedRoute requiredRoles={['student']}>
                <CoursesPage />
              </ProtectedRoute>
            } />
            <Route path="exams" element={
              <ProtectedRoute requiredRoles={['student']}>
                <ExamsPage />
              </ProtectedRoute>
            } />
            <Route path="exams/take/:examId" element={
              <ProtectedRoute requiredRoles={['student']}>
                <TakeExam />
              </ProtectedRoute>
            } />
            <Route path="exams/results/:examId" element={
              <ProtectedRoute requiredRoles={['student']}>
                <ExamResults />
              </ProtectedRoute>
            } />
            <Route path="teacher-classes" element={
              <ProtectedRoute requiredRoles={['teacher']}>
                <ClassesPage />
              </ProtectedRoute>
            } />
            
            {/* Teacher Routes */}
            <Route path="students" element={
              <ProtectedRoute requiredRoles={['teacher']}>
                <StudentsPage />
              </ProtectedRoute>
            } />
            <Route path="reports" element={
              <ProtectedRoute requiredRoles={['teacher', 'staff']}>
                <TeacherReportsPage />
              </ProtectedRoute>
            } />
            
            {/* Parent Routes */}
            <Route path="progress" element={
              <ProtectedRoute requiredRoles={['parent']}>
                <ProgressPage />
              </ProtectedRoute>
            } />
            <Route path="payments" element={
              <ProtectedRoute requiredRoles={['parent']}>
                <PaymentsPage />
              </ProtectedRoute>
            } />
            
            {/* Staff Routes */}
            <Route path="users" element={
              <ProtectedRoute requiredRoles={['staff']}>
                <UsersPage />
              </ProtectedRoute>
            } />
            <Route path="exams" element={
              <ProtectedRoute requiredRoles={['staff']}>
                <StaffExams />
              </ProtectedRoute>
            } />
            <Route path="exam-results" element={
              <ProtectedRoute requiredRoles={['staff']}>
                <ExamResults />
              </ProtectedRoute>
            } />
            <Route path="events" element={
              <ProtectedRoute requiredRoles={['staff']}>
                <EventsPage />
              </ProtectedRoute>
            } />
            <Route path="staff-reports" element={
              <ProtectedRoute requiredRoles={['staff']}>
                <StaffReportsPage />
              </ProtectedRoute>
            } />
            <Route path="assigned-classes" element={
              <ProtectedRoute requiredRoles={['staff']}>
                <AssignedClassesPage />
              </ProtectedRoute>
            } />
            <Route path="assigned-subjects" element={
              <ProtectedRoute requiredRoles={['staff']}>
                <AssignedSubjectsPage />
              </ProtectedRoute>
            } />
            <Route path="student-results" element={
              <ProtectedRoute requiredRoles={['staff']}>
                <StudentResultsPage />
              </ProtectedRoute>
            } />

            {/* Academic Routes */}
            <Route path="subjects" element={
              <ProtectedRoute requiredRoles={['teacher', 'student']}>
                <SubjectsPage />
              </ProtectedRoute>
            } />
            <Route path="timetable" element={
              <ProtectedRoute requiredRoles={['teacher', 'student', 'staff']}>
                <TimetablePage />
              </ProtectedRoute>
            } />
            <Route path="academic/classes/:id" element={
              <ProtectedRoute requiredRoles={['admin', 'teacher', 'staff']}>
                <ClassDetailPage />
              </ProtectedRoute>
            } />

            {/* Assignment Routes */}
            <Route path="assignments" element={
              <ProtectedRoute requiredRoles={['student', 'teacher', 'staff']}>
                <StudentAssignments />
              </ProtectedRoute>
            } />
            <Route path="assignments/create" element={
              <ProtectedRoute requiredRoles={['teacher', 'staff']}>
                <AssignmentCreate />
              </ProtectedRoute>
            } />
            <Route path="assignments/grade" element={
              <ProtectedRoute requiredRoles={['teacher', 'staff']}>
                <TeacherAssignments />
              </ProtectedRoute>
            } />

            {/* Exam Routes */}
            <Route path="exams" element={
              <ProtectedRoute requiredRoles={['student', 'teacher']}>
                <StudentExams />
              </ProtectedRoute>
            } />
            <Route path="exams/create" element={
              <ProtectedRoute requiredRoles={['teacher']}>
                <TeacherExams />
              </ProtectedRoute>
            } />
            <Route path="exams/schedule" element={
              <ProtectedRoute requiredRoles={['teacher']}>
                <TeacherExams />
              </ProtectedRoute>
            } />

            {/* Other Routes */}
            <Route path="grades" element={
              <ProtectedRoute requiredRoles={['student', 'teacher', 'parent']}>
                <GradesPage />
              </ProtectedRoute>
            } />
            <Route path="attendance" element={
              <ProtectedRoute requiredRoles={['student', 'teacher', 'parent']}>
                <AttendancePage />
              </ProtectedRoute>
            } />
            <Route path="messages" element={
              <ProtectedRoute requiredRoles={['student', 'teacher', 'parent', 'staff']}>
                <MessagesPage />
              </ProtectedRoute>
            } />
            <Route path="messages/compose" element={
              <ProtectedRoute>
                <ComposeMessage />
              </ProtectedRoute>
            } />
            <Route path="messages/:id" element={
              <ProtectedRoute>
                <MessageThread />
              </ProtectedRoute>
            } />
            <Route path="announcements" element={
              <ProtectedRoute requiredRoles={['student', 'teacher', 'parent', 'staff']}>
                <AnnouncementsPage />
              </ProtectedRoute>
            } />
            
            <Route path="notifications" element={
              <ProtectedRoute>
                <NotificationsPage />
              </ProtectedRoute>
            } />
            <Route path="library" element={
              <ProtectedRoute requiredRoles={['student', 'teacher', 'parent']}>
                <LibraryPage />
              </ProtectedRoute>
            } />
            <Route path="library/:id" element={
              <ProtectedRoute>
                <ResourceDetail />
              </ProtectedRoute>
            } />
            <Route path="library/upload" element={
              <ProtectedRoute requiredRoles={['teacher', 'staff']}>
                <ResourceUpload />
              </ProtectedRoute>
            } />
            <Route path="profile" element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            } />
            <Route path="settings" element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            } />

            {/* Parent-specific routes */}
            <Route path="manage-children" element={
              <ProtectedRoute requiredRoles={['parent']}>
                <ManageChildren />
              </ProtectedRoute>
            } />

            {/* Assignments */}
            <Route path="assignments/:id" element={
              <ProtectedRoute requiredRoles={['student', 'teacher', 'staff']}>
                <AssignmentDetail />
              </ProtectedRoute>
            } />
            <Route path="assignments/create" element={
              <ProtectedRoute requiredRoles={['teacher', 'staff']}>
                <AssignmentCreate />
              </ProtectedRoute>
            } />
            <Route path="assignments/:id/grade" element={
              <ProtectedRoute requiredRoles={['teacher', 'staff']}>
                <AssignmentGrade />
              </ProtectedRoute>
            } />

            {/* Exams */}
            <Route path="exams/:id" element={
              <ProtectedRoute requiredRoles={['student', 'teacher', 'staff']}>
                <ExamDetail />
              </ProtectedRoute>
            } />
            <Route path="exams/create" element={
              <ProtectedRoute requiredRoles={['teacher']}>
                <ExamCreate />
              </ProtectedRoute>
            } />
            <Route path="exams/:id/results" element={
              <ProtectedRoute requiredRoles={['teacher', 'student']}>
                <ExamResults />
              </ProtectedRoute>
            } />

            {/* Attendance */}
            <Route path="attendance/analytics" element={
              <ProtectedRoute requiredRoles={['teacher', 'staff']}>
                <AttendanceAnalytics />
              </ProtectedRoute>
            } />
            <Route path="attendance/mark" element={
              <ProtectedRoute requiredRoles={['teacher']}>
                <MarkAttendance />
              </ProtectedRoute>
            } />

            {/* 404 Route for Dashboard */}
            <Route path="*" element={<NotFound />} />
          </Route>

          {/* Redirect old dashboard routes */}
          <Route path="/student-dashboard" element={<Navigate to="/dashboard/student" replace />} />
          <Route path="/teacher-dashboard" element={<Navigate to="/dashboard/teacher" replace />} />
          <Route path="/parent-dashboard" element={<Navigate to="/dashboard/parent" replace />} />
          <Route path="/staff-dashboard" element={<Navigate to="/dashboard/staff" replace />} />

          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </div>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;